import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('正在登录中...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for error in URL hash (Supabase returns errors in hash)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const errorDescription = hashParams.get('error_description');
        const type = hashParams.get('type');
        
        if (errorDescription) {
          setError(decodeURIComponent(errorDescription));
          return;
        }

        // Handle password recovery redirect
        if (type === 'recovery') {
          setMessage('正在验证重置链接...');
          // Redirect to reset password page with the hash params
          navigate(`/auth/reset-password${window.location.hash}`, { replace: true });
          return;
        }

        // Handle email confirmation
        if (type === 'signup' || type === 'email_change') {
          setMessage('正在验证邮箱...');
        }

        // Get the session - Supabase automatically handles the OAuth callback
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          setError(sessionError.message || '登录失败');
          return;
        }

        if (session) {
          // Session exists, initialize auth store and redirect
          await initialize();
          navigate('/', { replace: true });
        } else {
          // No session, try to exchange the code
          // This handles the case where the URL contains the auth code
          const urlParams = new URLSearchParams(window.location.search);
          const code = urlParams.get('code');

          if (code) {
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

            if (exchangeError) {
              // If exchange fails, try to get session again (might already be set)
              const { data: { session: retrySession } } = await supabase.auth.getSession();
              
              if (retrySession) {
                await initialize();
                navigate('/', { replace: true });
              } else {
                setError('登录失败：无法获取会话');
              }
            } else {
              await initialize();
              navigate('/', { replace: true });
            }
          } else {
            // Try getting session one more time with hash params
            const accessToken = hashParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token');

            if (accessToken) {
              const { error: setSessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken || '',
              });

              if (setSessionError) {
                setError('登录失败：无效的认证信息');
              } else {
                await initialize();
                navigate('/', { replace: true });
              }
            } else {
              setError('登录失败：无法获取会话');
            }
          }
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('登录过程中发生错误');
      }
    };

    handleCallback();
  }, [navigate, initialize]);

  if (error) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="glass-card p-8 max-w-md text-center">
          <div className="text-5xl mb-4">😕</div>
          <h2 className="text-xl font-bold mb-2">认证失败</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary"
          >
            返回登录
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-400">{message}</p>
      </div>
    </div>
  );
}
